/**
 * The design system, as one import.
 *
 * ```ts
 * import { DS_CSS } from './ds';
 * document.head.appendChild(Object.assign(document.createElement('style'), { textContent: DS_CSS }));
 * document.getElementById('app')!.classList.add('ui-root');
 * ```
 *
 * That is the whole integration. There is no runtime, no build step, no
 * dependency and nothing to configure: `DS_CSS` is a string, `.ui-root` is the
 * class it is scoped to, and everything else is `<div class="panel">`.
 *
 * **The order below is the cascade and is not negotiable.** Tokens declare the
 * vocabulary; base spends it on element defaults; primitives spend it on
 * components; the application comes after all three and may override any of
 * them at equal specificity because it is later. Reverse any two and an
 * application rule stops winning, or a primitive resolves a `var()` that has
 * not been declared yet.
 *
 * See docs/28 for the token table, the component gallery, the recommendations,
 * and the audit of everywhere this application does not yet use its own system.
 */

import { BASE_CSS } from './base';
import { PRIMITIVES_CSS } from './primitives';
import { TOKENS_CSS } from './tokens';

export { BASE_CSS } from './base';
export { PRIMITIVES_CSS } from './primitives';
export { TOKENS, TOKENS_CSS, TOKEN_VALUE, UI_FS_BASE_PX } from './tokens';
export type { Token } from './tokens';
/**
 * The prose is re-exported for the gallery and the tests, and is deliberately
 * *not* pulled in by `DS_CSS`: a consumer that only mounts the sheet never
 * touches `tokenDocs.ts`, so the bundler drops seven kilobytes of English from
 * the application entry. docs/28 §9 has the measured delta.
 */
export { GROUP_BLURB, TOKEN_DOCS, TOKEN_GROUPS } from './tokenDocs';
export type { TokenDoc, TokenGroup } from './tokenDocs';

/** The three layers, named, in cascade order — for the docs and the tests. */
export const DS_LAYERS: readonly (readonly [name: string, css: string])[] = [
  ['tokens', TOKENS_CSS],
  ['base', BASE_CSS],
  ['primitives', PRIMITIVES_CSS],
];

/** The whole design system as one stylesheet. */
export const DS_CSS = DS_LAYERS.map(([, css]) => css).join('');

/**
 * The class the system is scoped to. Every rule it ships either *is* this
 * selector, is a descendant of it, or is a component class the host opts into.
 */
export const DS_ROOT_CLASS = 'ui-root';

/**
 * Mount the sheet, once.
 *
 * Idempotent by id: a second root, a re-entered scene or a hot reload must not
 * end up with two copies of the same rules, because the second copy silently
 * wins every tie and makes source order in *this* file stop being the cascade.
 */
export function injectDsStyle(doc: Document = document, id = 'ds-style'): void {
  if (doc.getElementById(id)) return;
  const s = doc.createElement('style');
  s.id = id;
  s.textContent = DS_CSS;
  doc.head.appendChild(s);
}
